import { CaretDown, PaperPlaneTilt, Stop } from "@phosphor-icons/react";
import { type KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PromptMessageType, PromptOutputFormat, ThoughtLevel } from "~/api/conversationTypes";
import { FACETS, PROMPT_MESSAGE_TYPES, PROMPT_OUTPUT_FORMATS } from "~/api/conversationTypes";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";
import { Tooltip } from "~/foundation/components/Tooltip";
import { cn } from "~/foundation/utils/cn";

export interface ComposerValue {
  content: string;
  promptType: PromptMessageType;
  outputFormat: PromptOutputFormat;
  thoughtLevel: ThoughtLevel;
  facet: string;
  /** Empty = broadcast to every assigned thinker, in their order. */
  targetThinkerIds: string[];
}

export const EMPTY_COMPOSER: ComposerValue = {
  content: "",
  promptType: "ask",
  outputFormat: "freeform",
  thoughtLevel: "medium",
  facet: "",
  targetThinkerIds: [],
};

const TYPE_LABEL: Record<PromptMessageType, string> = {
  ask: "Ask",
  instruct: "Instruct",
  critique: "Critique",
  decide: "Decide",
  summarize: "Summarize",
  checklist: "Checklist",
};

const FORMAT_LABEL: Record<PromptOutputFormat, string> = {
  freeform: "Freeform",
  bullets: "Bullets",
  checklist: "Checklist",
  decision_log: "Decision log",
  markdown: "Markdown",
};

const LEVEL: Array<{ value: ThoughtLevel; label: string; glyph: string }> = [
  { value: "low", label: "Quick", glyph: "⚡" },
  { value: "medium", label: "Balanced", glyph: "⚖️" },
  { value: "high", label: "Deep", glyph: "🧠" },
];

/**
 * The composer (W8 §B.3).
 *
 * kms collected `thoughtLevel`, `promptType`, `outputFormat` and the facet and **never sent
 * them** — the handler that would have was dead (W8-15). Every knob here reaches the wire:
 * a targeted or option-bearing turn goes through `POST .../prompts`, which is live and takes
 * all of them; a plain one streams. Shipping controls that do nothing is what created that
 * defect, so there are none here.
 */
export function Composer({
  value,
  onChange,
  onSend,
  onStop,
  busy,
  disabledReason,
  participants,
}: {
  value: ComposerValue;
  onChange: (next: ComposerValue) => void;
  onSend: () => void;
  onStop: () => void;
  busy: boolean;
  /** When set, sending is refused and this says why — never a silently inert button. */
  disabledReason: string | null;
  participants: Array<{ id: string; name: string }>;
}) {
  const { t } = useTranslation();
  const [optionsOpen, setOptionsOpen] = useState(false);

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (!disabledReason && value.content.trim()) {
        onSend();
      }
    }
  };

  const toggleTarget = (id: string) => {
    onChange({
      ...value,
      targetThinkerIds: value.targetThinkerIds.includes(id)
        ? value.targetThinkerIds.filter((entry) => entry !== id)
        : [...value.targetThinkerIds, id],
    });
  };

  return (
    <div className="flex flex-col gap-2 border-t border-divider pt-2">
      {disabledReason ? (
        <Text variant="small" className="text-warning">
          {disabledReason}
        </Text>
      ) : null}

      <div className="flex flex-wrap items-center gap-1">
        {PROMPT_MESSAGE_TYPES.slice(0, 3).map((entry) => (
          <button
            key={entry}
            type="button"
            onClick={() => onChange({ ...value, promptType: entry })}
            className={cn(
              "rounded-sm px-2 py-0.5 text-caption",
              value.promptType === entry
                ? "bg-accent text-accent-text"
                : "bg-bg-secondary text-text-secondary hover:bg-hover-bg",
            )}
          >
            {t(TYPE_LABEL[entry])}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setOptionsOpen((open) => !open)}
          aria-expanded={optionsOpen}
          className="ms-auto inline-flex items-center gap-1 text-caption text-text-tertiary hover:text-text"
        >
          {value.targetThinkerIds.length === 0
            ? t("All thinkers")
            : t("{{count}} thinkers", { count: value.targetThinkerIds.length })}
          <CaretDown size={12} aria-hidden className="rtl:rotate-180" />
        </button>
      </div>

      {optionsOpen ? (
        <div className="flex flex-col gap-2 rounded-sm border border-divider bg-bg-secondary p-2">
          <div className="flex flex-wrap gap-1" role="group" aria-label={t("Message type")}>
            {PROMPT_MESSAGE_TYPES.map((entry) => (
              <button
                key={entry}
                type="button"
                onClick={() => onChange({ ...value, promptType: entry })}
                className={cn(
                  "rounded-sm px-2 py-0.5 text-caption",
                  value.promptType === entry
                    ? "bg-accent text-accent-text"
                    : "bg-bg text-text-secondary hover:bg-hover-bg",
                )}
              >
                {t(TYPE_LABEL[entry])}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-1" role="group" aria-label={t("Output format")}>
            {PROMPT_OUTPUT_FORMATS.map((entry) => (
              <button
                key={entry}
                type="button"
                onClick={() => onChange({ ...value, outputFormat: entry })}
                className={cn(
                  "rounded-sm px-2 py-0.5 text-caption",
                  value.outputFormat === entry
                    ? "bg-accent text-accent-text"
                    : "bg-bg text-text-secondary hover:bg-hover-bg",
                )}
              >
                {t(FORMAT_LABEL[entry])}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-1" role="group" aria-label={t("Thought depth")}>
            {LEVEL.map((entry) => (
              <button
                key={entry.value}
                type="button"
                onClick={() => onChange({ ...value, thoughtLevel: entry.value })}
                className={cn(
                  "rounded-sm px-2 py-0.5 text-caption",
                  value.thoughtLevel === entry.value
                    ? "bg-accent text-accent-text"
                    : "bg-bg text-text-secondary hover:bg-hover-bg",
                )}
              >
                <span aria-hidden>{entry.glyph} </span>
                {t(entry.label)}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-1" role="group" aria-label={t("Facet tag")}>
            {["", ...FACETS].map((entry) => (
              <button
                key={entry || "no-facet"}
                type="button"
                onClick={() => onChange({ ...value, facet: entry })}
                className={cn(
                  "rounded-sm px-2 py-0.5 text-caption",
                  value.facet === entry
                    ? "bg-accent text-accent-text"
                    : "bg-bg text-text-secondary hover:bg-hover-bg",
                )}
              >
                {entry === "" ? t("None") : t(entry)}
              </button>
            ))}
          </div>

          {participants.length > 0 ? (
            <div className="flex flex-wrap gap-1" role="group" aria-label={t("Ask which thinkers")}>
              {participants.map((participant) => (
                <button
                  key={participant.id}
                  type="button"
                  onClick={() => toggleTarget(participant.id)}
                  className={cn(
                    "rounded-sm px-2 py-0.5 text-caption",
                    value.targetThinkerIds.includes(participant.id)
                      ? "bg-accent text-accent-text"
                      : "bg-bg text-text-secondary hover:bg-hover-bg",
                  )}
                >
                  @{participant.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <textarea
        value={value.content}
        onChange={(event) => onChange({ ...value, content: event.target.value })}
        onKeyDown={onKeyDown}
        rows={3}
        aria-label={t("Message")}
        placeholder={t("Ask the thinkers… (⌘↵ to send)")}
        disabled={Boolean(disabledReason)}
        className="w-full rounded-sm border border-input-border bg-bg p-2 text-body text-text focus:outline-none disabled:opacity-60"
      />

      <div className="flex items-center gap-2">
        {busy ? (
          <Button size="sm" variant="neutral" icon={Stop} onClick={onStop}>
            {t("Stop")}
          </Button>
        ) : (
          <Tooltip content={disabledReason ?? t("Send with ⌘↵")}>
            <Button
              size="sm"
              icon={PaperPlaneTilt}
              disabled={Boolean(disabledReason) || value.content.trim().length === 0}
              onClick={onSend}
            >
              {t("Send")}
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
